import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Trash } from '@phosphor-icons/react';
import type { ExpectedField, ImportBatchRow, ImportTarget, RowChangeSet } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { clientRowErrors } from './importWizardHelpers';
import { reviewRowStatus, type ReviewRowStatus } from './reviewRowStatus';
import {
  deletesChangeSet,
  departedRows,
  editShortcut,
  isLockedCell,
  reviewColumnLabel,
  setDraftCell,
  toggleDraftDelete,
  withDepartedRows,
  REVIEW_SCROLL_MAX_HEIGHT_CLASS,
  type DepartedRow,
  type GridDraft,
} from './reviewGridDraft';
import { ReviewCell, ReviewEditCell, ReviewLockedCell } from './ReviewCell';
import { ReviewStatusPill } from './ReviewStatusPill';
import { useRowNumberDisplay } from './rowNumberDisplay';
import { VersionToggle } from './VersionToggle';

// One height in view and edit mode, so the virtualiser never re-measures.
const ROW_HEIGHT_PX = 44;
const OVERSCAN_ROWS = 8;
const ROW_FADE_MS = 300;
// The fade lasts exactly as long as the departed row is kept mounted.
const FADE_STYLE: CSSProperties = { animationDuration: `${ROW_FADE_MS}ms` };
const ICON_SIZE = 16;
const ROW_COLUMN_PX = 56;
const STATUS_COLUMN_PX = 128;
const VERSION_COLUMN_PX = 148;
const FIELD_COLUMN_MIN_PX = 140;
const ACTIONS_COLUMN_PX = 48;
// aria-rowindex is 1-based and the header row takes index 1.
const FIRST_BODY_ROW_INDEX = 2;
const HEADER_CELL_CLASS = 'flex items-center px-2 text-caption-medium text-muted-foreground';
// Field headers start where their cells' text starts (see ReviewCell).
const FIELD_HEADER_CELL_CLASS = 'flex items-center px-4 text-caption-medium text-muted-foreground';

export interface ImportReviewGridProps {
  /** Default 'pipeline'; only the status wording differs per target. */
  target?: ImportTarget;
  rows: ImportBatchRow[];
  /** The grid's columns only: live validation checks exactly these fields. */
  fields: ExpectedField[];
  campaignPlatform: string;
  /** Decides whether a ₹0 price counts as pricing a deliverable. */
  payoutModel?: string;
  showVersion: boolean;
  /** Rows only fade out when they leave the SAME tab, not on a tab switch. */
  fadeKey: string;
  emptyText?: string;
  busy: boolean;
  editing: boolean;
  draft: GridDraft | null;
  /** The cell the admin entered edit mode from, so its input takes focus. */
  onStartEdit: (cell?: EditTarget) => void;
  /** Which cell should grab focus as it mounts; null once it has. */
  focusCell?: EditTarget | null;
  onFocusHandled?: () => void;
  onDraftChange: (draft: GridDraft) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onSaveRows: (changes: RowChangeSet, message: string) => void;
}

export interface EditTarget {
  rowNumber: number;
  fieldKey: string;
}

interface GridLayout {
  template: string;
  minWidth: number;
}

function fixedColumns(showVersion: boolean): number[] {
  if (showVersion) return [ROW_COLUMN_PX, STATUS_COLUMN_PX, VERSION_COLUMN_PX];
  return [ROW_COLUMN_PX, STATUS_COLUMN_PX];
}

// Field columns share the spare width but never shrink below their minimum;
// past that the table scrolls sideways instead of squashing values.
function gridLayout(fieldCount: number, showVersion: boolean): GridLayout {
  const leading = fixedColumns(showVersion);
  const fieldColumns = Array.from({ length: fieldCount }, () => `minmax(${FIELD_COLUMN_MIN_PX}px, 1fr)`);
  const template = [...leading.map((px) => `${px}px`), ...fieldColumns, `${ACTIONS_COLUMN_PX}px`].join(' ');
  const fixedWidth = leading.reduce((sum, px) => sum + px, ACTIONS_COLUMN_PX);
  return { template, minWidth: fixedWidth + fieldCount * FIELD_COLUMN_MIN_PX };
}

interface FadeState {
  rows: ImportBatchRow[];
  fadeKey: string;
  fading: DepartedRow[];
}

function useFadingRows(rows: ImportBatchRow[], fadeKey: string): { shown: ImportBatchRow[]; fading: Set<number> } {
  const [state, setState] = useState<FadeState>({ rows, fadeKey, fading: [] });
  if (state.rows !== rows || state.fadeKey !== fadeKey) {
    const fading = state.fadeKey === fadeKey ? departedRows(state.rows, rows) : [];
    setState({ rows, fadeKey, fading });
  }
  useEffect(() => {
    if (state.fading.length === 0) return;
    const timer = setTimeout(() => setState((s) => ({ ...s, fading: [] })), ROW_FADE_MS);
    return () => clearTimeout(timer);
  }, [state.fading]);
  const shown = useMemo(() => withDepartedRows(rows, state.fading), [rows, state.fading]);
  const fading = useMemo(() => new Set(state.fading.map(({ row }) => row.rowNumber)), [state.fading]);
  return { shown, fading };
}

interface HeaderRowProps {
  fields: ExpectedField[];
  campaignPlatform: string;
  showVersion: boolean;
  template: string;
}

function HeaderRow({ fields, campaignPlatform, showVersion, template }: HeaderRowProps) {
  return (
    <div role="row" aria-rowindex={1} className="grid h-10 border-b border-border" style={{ gridTemplateColumns: template }}>
      <div role="columnheader" className={HEADER_CELL_CLASS}>Row</div>
      <div role="columnheader" className={HEADER_CELL_CLASS}>Status</div>
      {showVersion && <div role="columnheader" className={HEADER_CELL_CLASS}>Version</div>}
      {fields.map((field) => (
        <div key={field.key} role="columnheader" className={FIELD_HEADER_CELL_CLASS}>
          <span className="truncate">{reviewColumnLabel(field, campaignPlatform)}</span>
          {field.required && <span className="text-destructive" aria-hidden="true">*</span>}
        </div>
      ))}
      <div role="columnheader" className={HEADER_CELL_CLASS}>
        <span className="sr-only">Actions</span>
      </div>
    </div>
  );
}

const NO_CHANGES_STATUS: ReviewRowStatus = 'no-changes';
const VERSION_TEXT_CELL_CLASS = 'flex items-center px-2 text-caption-regular text-muted-foreground';
const NEW_CREATOR_VERSION_TEXT = 'New';
const SAME_AS_CAMPAIGN_VERSION_TEXT = 'Same as campaign';

// A toggle only where there is a choice to make: a new creator has no
// current version, and a row identical to the campaign's has nothing to
// choose between (the server refuses a decision on it).
function VersionCell({ row, busy, onSaveRows }: { row: ImportBatchRow; busy: boolean; onSaveRows: ImportReviewGridProps['onSaveRows'] }) {
  if (row.rosterMatch === null) return <div role="cell" className={VERSION_TEXT_CELL_CLASS}>{NEW_CREATOR_VERSION_TEXT}</div>;
  if (reviewRowStatus(row) === NO_CHANGES_STATUS) return <div role="cell" className={VERSION_TEXT_CELL_CLASS}>{SAME_AS_CAMPAIGN_VERSION_TEXT}</div>;
  return (
    <div role="cell" className="flex items-center px-2">
      <VersionToggle row={row} busy={busy} onSaveRows={onSaveRows} />
    </div>
  );
}

interface GridRowProps extends Pick<ImportReviewGridProps, 'target' | 'fields' | 'campaignPlatform' | 'payoutModel' | 'showVersion' | 'busy' | 'editing' | 'onStartEdit' | 'onSaveRows'> {
  onFocusHandled: () => void;
  row: ImportBatchRow;
  rowIndex: number;
  template: string;
  /** The virtual row's height and offset, as numbers: a style object would be
   *  a new prop on every render and defeat the row's memoisation. */
  size: number;
  start: number;
  fading: boolean;
  draftValues: Record<string, string> | undefined;
  deleted: boolean;
  /** Only the row holding the focus target gets one, so the memoised rows
   *  that don't hold it keep their props. */
  focusFieldKey: string | null;
  onCellChange: (rowNumber: number, key: string, value: string) => void;
  onDelete: (rowNumber: number) => void;
}

function FieldCell(props: GridRowProps & { field: ExpectedField; error: string | undefined }) {
  const { row, field, draftValues, campaignPlatform } = props;
  if (!props.editing || !draftValues) {
    return (
      <ReviewCell
        row={row}
        field={field}
        campaignPlatform={campaignPlatform}
        payoutModel={props.payoutModel}
        onStartEdit={() => props.onStartEdit({ rowNumber: row.rowNumber, fieldKey: field.key })}
      />
    );
  }
  if (isLockedCell(row, field)) return <ReviewLockedCell row={row} field={field} />;
  return (
    <ReviewEditCell
      rowNumber={row.rowNumber}
      field={field}
      label={reviewColumnLabel(field, campaignPlatform)}
      value={draftValues[field.key] ?? ''}
      error={props.error}
      deleted={props.deleted}
      focusOnMount={props.focusFieldKey === field.key}
      onFocused={props.onFocusHandled}
      onChange={(value) => props.onCellChange(row.rowNumber, field.key, value)}
    />
  );
}

function rowStyle(size: number, start: number, template: string, fading: boolean): CSSProperties {
  const base: CSSProperties = { height: size, transform: `translateY(${start}px)`, gridTemplateColumns: template };
  if (!fading) return base;
  return { ...base, ...FADE_STYLE };
}

const NO_ERRORS: Record<string, string> = {};

/** The server's errors on cells the draft still holds as saved: some rules
 *  run only server-side, and a cell must not look fixed before it is. */
function unchangedServerErrors(row: ImportBatchRow, draftValues: Record<string, string>): Record<string, string> {
  const kept: Record<string, string> = {};
  for (const [key, message] of Object.entries(row.validationErrors)) {
    if ((draftValues[key] ?? '') === (row.values[key] ?? '')) kept[key] = message;
  }
  return kept;
}

/** Edit mode's live mirror of the server's rules for one row's draft. */
function draftErrors(props: GridRowProps): Record<string, string> {
  const { row, fields, editing, draftValues } = props;
  if (!editing || !draftValues) return NO_ERRORS;
  const live = clientRowErrors(draftValues, fields, props.campaignPlatform, {
    isNewCreator: row.rosterMatch === null,
    payoutModel: props.payoutModel,
  });
  return { ...unchangedServerErrors(row, draftValues), ...live };
}

// Memoised: typing in one cell changes only that row's draft values, so every
// other visible row (its cells, tooltips and live validation) is skipped.
const GridRow = memo(function GridRow(props: GridRowProps) {
  const { row, fields, template, fading, editing, deleted, busy } = props;
  const shownNumber = useRowNumberDisplay();
  const errors = draftErrors(props);
  return (
    <div
      role="row"
      aria-rowindex={props.rowIndex}
      style={rowStyle(props.size, props.start, template, fading)}
      className={cn(
        'group absolute inset-x-0 top-0 grid border-b border-border bg-card',
        fading && 'pointer-events-none animate-out fade-out fill-mode-forwards motion-reduce:animate-none',
        deleted && 'bg-muted text-muted-foreground line-through',
      )}
    >
      <div role="rowheader" className="flex items-center px-2 font-mono text-caption-regular text-muted-foreground">{shownNumber(row.rowNumber)}</div>
      <div role="cell" className="flex items-center px-2">
        <ReviewStatusPill status={reviewRowStatus(row)} target={props.target} />
      </div>
      {props.showVersion && <VersionCell row={row} busy={busy} onSaveRows={props.onSaveRows} />}
      {fields.map((field) => (
        <FieldCell key={field.key} {...props} field={field} error={errors[field.key]} />
      ))}
      <div role="cell" className="flex items-center justify-center">
        <button
          type="button"
          aria-label={`Delete row ${shownNumber(row.rowNumber)}`}
          aria-pressed={editing ? deleted : undefined}
          title="Delete row"
          disabled={busy}
          onClick={() => props.onDelete(row.rowNumber)}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-50',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            (editing || deleted) && 'opacity-100',
            deleted && 'text-destructive',
          )}
        >
          <Trash size={ICON_SIZE} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
});

export function ImportReviewGrid(props: ImportReviewGridProps) {
  const { rows, fields, showVersion, fadeKey, editing, draft, focusCell, onDraftChange } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { shown, fading } = useFadingRows(rows, fadeKey);
  const virtualizer = useVirtualizer({
    count: shown.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: OVERSCAN_ROWS,
  });
  const { template, minWidth } = gridLayout(fields.length, showVersion);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!editing) return;
    const shortcut = editShortcut({ key: event.key, ctrlKey: event.ctrlKey, metaKey: event.metaKey, isComposing: event.nativeEvent.isComposing });
    if (shortcut === null) return;
    event.preventDefault();
    if (shortcut === 'save') props.onSaveEdit();
    else props.onCancelEdit();
  }

  // The memoised rows need callbacks whose identity never changes, so these
  // read the current props through a ref instead of closing over them. A
  // layout effect keeps the ref current before the next input event can fire.
  const { onSaveRows, onStartEdit, onFocusHandled } = props;
  const shownNumber = useRowNumberDisplay();
  const latest = useRef({ editing, draft, onDraftChange, onSaveRows, onStartEdit, onFocusHandled, shownNumber });
  useLayoutEffect(() => {
    latest.current = { editing, draft, onDraftChange, onSaveRows, onStartEdit, onFocusHandled, shownNumber };
  });

  const changeCell = useCallback((rowNumber: number, key: string, value: string) => {
    const now = latest.current;
    if (now.draft) now.onDraftChange(setDraftCell(now.draft, rowNumber, key, value));
  }, []);

  const deleteRow = useCallback((rowNumber: number) => {
    const now = latest.current;
    if (now.editing && now.draft) {
      now.onDraftChange(toggleDraftDelete(now.draft, rowNumber));
      return;
    }
    now.onSaveRows(deletesChangeSet([rowNumber]), `Row ${now.shownNumber(rowNumber)} deleted`);
  }, []);

  const startEdit = useCallback((cell?: EditTarget) => latest.current.onStartEdit(cell), []);
  const focusHandled = useCallback(() => latest.current.onFocusHandled?.(), []);

  const saveRows = useCallback((changes: RowChangeSet, message: string) => {
    latest.current.onSaveRows(changes, message);
  }, []);

  return (
    <div
      ref={scrollRef}
      onKeyDown={handleKeyDown}
      className={cn('relative min-h-0 overflow-auto rounded-lg border border-border bg-card', REVIEW_SCROLL_MAX_HEIGHT_CLASS)}
    >
      <div role="table" aria-label="Sheet rows" aria-rowcount={shown.length + 1} style={{ minWidth }}>
        <div role="rowgroup" className="sticky top-0 z-10 bg-card">
          <HeaderRow fields={fields} campaignPlatform={props.campaignPlatform} showVersion={showVersion} template={template} />
        </div>
        <div role="rowgroup" className="relative" style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((item) => {
            const row = shown[item.index];
            if (row === undefined) return null;
            return (
              <GridRow
                key={row.rowNumber}
                target={props.target}
                fields={fields}
                campaignPlatform={props.campaignPlatform}
                payoutModel={props.payoutModel}
                showVersion={showVersion}
                busy={props.busy}
                editing={editing}
                onStartEdit={startEdit}
                onFocusHandled={focusHandled}
                focusFieldKey={focusCell?.rowNumber === row.rowNumber ? focusCell.fieldKey : null}
                onSaveRows={saveRows}
                row={row}
                rowIndex={item.index + FIRST_BODY_ROW_INDEX}
                template={template}
                size={item.size}
                start={item.start}
                fading={fading.has(row.rowNumber)}
                draftValues={draft?.values[row.rowNumber]}
                deleted={draft?.deletes.has(row.rowNumber) ?? false}
                onCellChange={changeCell}
                onDelete={deleteRow}
              />
            );
          })}
        </div>
      </div>
      {shown.length === 0 && <p className="px-4 py-8 text-center text-body-sm-regular text-muted-foreground">{props.emptyText}</p>}
    </div>
  );
}
